/**
 * Typed client for /api/ap/*.
 *
 * Shapes mirror api/src/ap/store.ts. api/ and src/ are separate TypeScript
 * projects, so drift here is not a build error — it is an undefined at runtime.
 * If you change a field name on the server, change it here in the same commit.
 */
import { apiFetch } from '../api'

export type InvoiceState = 'InFlight' | 'Confirmed' | 'AutoFailed' | 'Ignored'

export type ApMode = 'dry_run' | 'review' | 'live'

/** The app's opinion on whether an invoice is safe to send. needs_fix is human-only. */
export type Verdict = 'ready' | 'needs_review' | 'unreadable' | 'needs_fix'

export const VERDICT_LABELS: Record<Verdict, string> = {
  ready: 'ok to send',
  needs_review: 'needs review',
  unreadable: "can't be read",
  needs_fix: 'needs a code fix',
}

export const FAIL_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'no_attachment', label: 'Nothing usable attached' },
  { key: 'unreadable_scan', label: 'Attached but unreadable' },
  { key: 'multi_page', label: 'Several invoices in one file' },
  { key: 'supplier_ambiguous', label: 'Supplier matched more than one account' },
  { key: 'supplier_missing', label: 'No matching iplicit supplier' },
  { key: 'supplier_not_read', label: 'Supplier name not read off the page' },
  { key: 'no_invoice_number', label: 'No invoice number' },
  { key: 'no_total', label: 'No total' },
  { key: 'credit_note', label: 'Credit note, not an invoice' },
  { key: 'statement', label: 'Statement, not an invoice' },
  { key: 'not_an_invoice', label: 'Not an invoice at all' },
  { key: 'iplicit_rejected', label: 'iplicit refused it' },
  { key: 'forward_failed', label: 'Forward failed' },
  { key: 'other', label: 'Something else' },
]

export interface ProposalLine { description: string | null; net: number | null; quantity: number | null }

export interface Proposal {
  supplier: string | null
  theirDocNo: string | null
  invoiceDate: string | null
  netTotal: number | null
  vatTotal: number | null
  grossTotal: number | null
  lines: ProposalLine[]
}

export interface SupplierResolution {
  status: 'confident' | 'ambiguous' | 'none'
  id: string | null
  name: string | null
  score: number | null
  /** Absent on rows resolved before 30 Jul 2026 — the UI falls back to a code lookup. */
  candidates: Array<{ contactAccountId?: string; description: string; code: string; score: number }>
}

export type EventKind =
  | 'arrived' | 'classified' | 'forwarded' | 'forward_failed'
  | 'match_checked' | 'matched' | 'window_expired'
  | 'inject_attempted' | 'injected' | 'inject_failed'
  | 'move_requested' | 'move_confirmed' | 'move_mismatch'
  | 'ignored' | 'unignored' | 'digest_sent' | 'note'
  | 'resolved' | 'verdict_set' | 'rechecked' | 'sent_manually' | 'categorised'
  | 'already_in_iplicit' | 'linked_existing'

export interface Invoice {
  id: number
  immutableMessageId: string
  internetMessageId: string | null
  senderEmail: string | null
  supplier: string | null
  theirDocNo: string | null
  gross: number | null
  docType: string | null
  confidence: number | null
  state: InvoiceState
  createMethod: 'forward' | 'inject' | 'existing' | null
  iplicitDocNo: string | null
  isDuplicate: boolean
  failReason: string | null
  dryRun: boolean
  arrivedUtc: string
  forwardedUtc: string | null
  failedUtc: string | null
  injectedUtc: string | null
  settledUtc: string | null
  ignoredUtc: string | null
  ignoredBy: string | null
  ignoreReason: string | null
  checks: number
  lastCheckUtc: string | null
  folderExpectedName: string | null
  folderActualName: string | null
  folderCheckedUtc: string | null
  placementMismatch: boolean
  verdict: Verdict | null
  verdictBy: string | null
  verdictUtc: string | null
  failCategory: string | null
  failCategoryBy: string | null
  reviewNote: string | null
  proposal: Proposal | null
  supplierResolution: SupplierResolution | null
  resolvedUtc: string | null
  sentManuallyBy: string | null
  sentManuallyUtc: string | null
  /** Per-invoice coding override, from the console or a remembered mapping. */
  nominalCode: string | null
  /** Set when a human picked the supplier rather than the matcher finding it. */
  supplierSetBy: string | null
}

export interface ApEvent {
  at: string
  kind: EventKind
  actor: string
  detail: string | null
}

export interface FolderRef { id: string | null; name: string }

export interface ApSettings {
  enabled: boolean
  mode: ApMode
  injectEnabled: boolean
  injectMode: ApMode
  windowMinutes: number
  perRunCap: number
  folders: { source: FolderRef; review: FolderRef; done: FolderRef; failed: FolderRef; ignored: FolderRef }
  updatedUtc: string
  updatedBy: string | null
}

export interface Counts {
  inFlight: number
  confirmed: number
  autoFailed: number
  ignored: number
  needsAttention: number
  oldestInFlightUtc: string | null
}

export interface MailFolder {
  id: string
  displayName: string
  path: string
  totalItemCount: number
  unreadItemCount: number
  childFolderCount: number
}

export type FolderKey = 'source' | 'review' | 'done' | 'failed' | 'ignored'

export const FOLDER_LABELS: Array<{ key: FolderKey; label: string; help: string }> = [
  { key: 'source', label: 'Processing invoices', help: 'The ONLY folder the app reads. Drag an invoice in here to have it processed. Nothing in the Inbox is ever touched.' },
  { key: 'review', label: 'Under review', help: 'Where an invoice waits after being forwarded, until iplicit confirms it.' },
  { key: 'done', label: 'Processed', help: 'Confirmed and finished.' },
  { key: 'failed', label: 'Auto failed', help: 'Could not be got into iplicit. These need entering by hand.' },
  { key: 'ignored', label: 'Ignored', help: 'Filed here when you press Ignore, so the processing folder drains back to empty.' },
]

export interface ReconcileStatus {
  enabled: boolean
  nextRunUtc: string | null
  lastRunUtc: string | null
  lastStatus: string | null
  lastMessage: string | null
}

export interface CategoryCount { category: string; n: number }

export function getOverview(): Promise<{
  settings: ApSettings
  counts: Counts
  job: ReconcileStatus | null
  categories: CategoryCount[]
  mailbox: string
}> {
  return apiFetch('/api/ap')
}

/** Send this one invoice to iplicit now, whatever mode the app is in. */
export function sendInvoice(id: number): Promise<{
  ok: boolean
  message: string
  invoice: Invoice
  events: ApEvent[]
}> {
  return apiFetch(`/api/ap/invoices/${id}/send`, { method: 'POST', body: JSON.stringify({}) })
}

/** Re-read the invoice after a fix and update its verdict. */
export function recheckInvoice(id: number): Promise<{ verdict: Verdict; invoice: Invoice; events: ApEvent[] }> {
  return apiFetch(`/api/ap/invoices/${id}/recheck`, { method: 'POST', body: JSON.stringify({}) })
}

/**
 * Mark this invoice as already in iplicit, against a document someone else
 * created. Confirmed and proven, recorded as not ours.
 */
export function linkExisting(id: number, docNo: string, note?: string): Promise<{ invoice: Invoice; events: ApEvent[] }> {
  return apiFetch(`/api/ap/invoices/${id}/link-existing`, {
    method: 'POST',
    body: JSON.stringify({ docNo, note }),
  })
}

/** Override the app's verdict and/or its guess at the failure category. */
export function patchReview(
  id: number,
  body: { verdict?: Verdict; category?: string | null; note?: string },
): Promise<{ invoice: Invoice; events: ApEvent[] }> {
  return apiFetch(`/api/ap/invoices/${id}/review`, { method: 'PATCH', body: JSON.stringify(body) })
}

/**
 * Run the pick-up-and-chase pass now, rather than waiting for the next 15-minute
 * slot. Goes through the scheduler's own run-now endpoint, so the run is recorded
 * in the job history exactly like a scheduled one.
 */
export function runNow(): Promise<{ id: string; lastRun: { status: string; message: string; durationMs: number } }> {
  return apiFetch('/api/scheduler/triggers/ap.reconcile/run', { method: 'POST' })
}

/** "in 4 min" / "in 2 hours" — how long until `iso`. */
export function until(iso: string | null): string {
  if (!iso) return '—'
  const mins = Math.ceil((Date.parse(iso) - Date.now()) / 60_000)
  if (Number.isNaN(mins)) return '—'
  if (mins <= 0) return 'due now'
  if (mins === 1) return 'in 1 min'
  if (mins < 60) return `in ${mins} min`
  const h = Math.floor(mins / 60)
  return `in ${h} hour${h === 1 ? '' : 's'}`
}

export function patchSettings(body: Record<string, unknown>): Promise<{ settings: ApSettings; counts: Counts }> {
  return apiFetch('/api/ap/settings', { method: 'PATCH', body: JSON.stringify(body) })
}

export function getFolders(): Promise<{
  mailbox: string
  folders: MailFolder[]
  sourceFolderExists: boolean
  sourceFolderCount: number | null
}> {
  return apiFetch('/api/ap/folders')
}

export function ensureFolder(key: FolderKey, name: string): Promise<{
  created: boolean
  folder: { id: string; name: string }
  settings: ApSettings
}> {
  return apiFetch('/api/ap/folders/ensure', { method: 'POST', body: JSON.stringify({ key, name }) })
}

export function getInvoices(params: { state?: InvoiceState; q?: string } = {}): Promise<{ invoices: Invoice[] }> {
  const qs = new URLSearchParams()
  if (params.state) qs.set('state', params.state)
  if (params.q) qs.set('q', params.q)
  const s = qs.toString()
  return apiFetch(`/api/ap/invoices${s ? `?${s}` : ''}`)
}

export function getNeedsAttention(): Promise<{ invoices: Invoice[]; windowMinutes: number }> {
  return apiFetch('/api/ap/invoices/needs-attention')
}

export function getInvoice(id: number): Promise<{ invoice: Invoice; events: ApEvent[] }> {
  return apiFetch(`/api/ap/invoices/${id}`)
}

export function ignoreInvoice(id: number, reason: string): Promise<{ invoice: Invoice; events: ApEvent[] }> {
  return apiFetch(`/api/ap/invoices/${id}/ignore`, { method: 'POST', body: JSON.stringify({ reason }) })
}

export function unignoreInvoice(id: number): Promise<{ invoice: Invoice; events: ApEvent[] }> {
  return apiFetch(`/api/ap/invoices/${id}/unignore`, { method: 'POST', body: JSON.stringify({}) })
}

export function verifyPlacement(id: number): Promise<{
  invoice: Invoice
  events: ApEvent[]
  expectedFolder: string
  actualFolder: string | null
}> {
  return apiFetch(`/api/ap/invoices/${id}/verify-placement`, { method: 'POST', body: JSON.stringify({}) })
}

// ─── display helpers ─────────────────────────────────────────────────────────

/** "22 Jul 07:45" in London time, with the full UTC value for the title attr. */
export function stamp(iso: string | null): { short: string; full: string } {
  if (!iso) return { short: '—', full: '' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { short: '—', full: '' }
  return {
    short: d.toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/London', hour12: false,
    }),
    full: `${iso} (UTC)`,
  }
}

/** "41 min" / "3h 12m" / "2 days" — how long since `iso`. */
export function since(iso: string | null): string {
  if (!iso) return '—'
  const mins = Math.floor((Date.now() - Date.parse(iso)) / 60_000)
  if (Number.isNaN(mins)) return '—'
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  if (h < 48) return `${h}h ${mins % 60}m`
  return `${Math.floor(h / 24)} days`
}

export function money(n: number | null): string {
  if (n === null) return '—'
  return n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
}

/**
 * How urgent an in-flight row is. Amber past two thirds of the window, red once
 * the window has passed — at which point the app will act on its own, so red is
 * your last chance to intervene.
 */
export function urgency(inv: Invoice, windowMinutes: number): 'none' | 'warn' | 'late' {
  // A dry-run row was never really forwarded, so it sits in flight indefinitely
  // by design. Ageing it would leave permanently red rows on the console after
  // any test session, which is exactly the noise this view exists to remove.
  if (inv.dryRun) return 'none'
  if (inv.placementMismatch) return 'late'
  if (inv.state !== 'InFlight') return 'none'
  const mins = (Date.now() - Date.parse(inv.arrivedUtc)) / 60_000
  if (mins >= windowMinutes) return 'late'
  if (mins >= windowMinutes * 0.66) return 'warn'
  return 'none'
}

export const EVENT_LABELS: Record<EventKind, string> = {
  arrived: 'Arrived in the processing folder',
  classified: 'Fields extracted',
  forwarded: 'Forwarded to iplicit',
  forward_failed: 'Forward failed',
  match_checked: 'Looked for it in iplicit',
  matched: 'Matched an iplicit document',
  window_expired: 'Unconfirmed past the window',
  inject_attempted: 'Attempted direct create',
  injected: 'Created directly in iplicit',
  inject_failed: 'Direct create failed',
  move_requested: 'Asked to file the email',
  move_confirmed: 'Email confirmed in place',
  move_mismatch: 'Email is NOT where it should be',
  resolved: 'Supplier looked up in iplicit',
  verdict_set: 'Status set by hand',
  rechecked: 'Re-read on request',
  sent_manually: 'Sent to iplicit by hand',
  categorised: 'Reason categorised',
  already_in_iplicit: 'Already in iplicit',
  linked_existing: 'Linked to an existing iplicit document',
  ignored: 'Ignored',
  unignored: 'Returned to the flow',
  digest_sent: 'Included in the morning digest',
  note: 'Note',
}

// ─── iplicit catalogues and remembered supplier choices ──────────────────────

export interface SupplierOption {
  contactAccountId: string
  code: string | null
  description: string
  defaultNominal: string | null
}

export interface NominalOption { code: string; description: string }

export interface CatalogStatus {
  suppliers: number
  nominals: number
  suppliersSyncedUtc: string | null
  nominalsSyncedUtc: string | null
}

export function searchSuppliers(q: string): Promise<{
  suppliers: SupplierOption[]
  catalog: CatalogStatus
  warning?: string
}> {
  return apiFetch(`/api/ap/suppliers?q=${encodeURIComponent(q)}`)
}

export function searchNominals(q: string): Promise<{ nominals: NominalOption[]; catalog: CatalogStatus }> {
  return apiFetch(`/api/ap/nominals?q=${encodeURIComponent(q)}`)
}

/**
 * Set the iplicit supplier for one invoice by hand, optionally remembering it.
 *
 * Remembering is what makes snagging finite: the matcher cannot safely accept a
 * 0.50 score, so a person decides once and every later invoice from that sender
 * resolves on its own.
 */
export function setSupplier(
  id: number,
  body: { contactAccountId: string; name: string; nominalCode?: string | null; remember?: boolean; note?: string },
): Promise<{ invoice: Invoice; events: ApEvent[] }> {
  return apiFetch(`/api/ap/invoices/${id}/set-supplier`, { method: 'POST', body: JSON.stringify(body) })
}

export function setNominal(id: number, code: string | null): Promise<{ invoice: Invoice; events: ApEvent[] }> {
  return apiFetch(`/api/ap/invoices/${id}/set-nominal`, { method: 'POST', body: JSON.stringify({ code }) })
}
